const VOICE_NOTE_ENHANCED_CONSTRAINTS: MediaStreamConstraints = {
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
  },
};

const VOICE_NOTE_BASIC_CONSTRAINTS: MediaStreamConstraints = {
  audio: true,
};

type MediaDevicesLike = Pick<MediaDevices, "getUserMedia">;

export const getVoiceNoteMediaConstraints = (): MediaStreamConstraints =>
  VOICE_NOTE_ENHANCED_CONSTRAINTS;

export const getBasicVoiceNoteMediaConstraints = (): MediaStreamConstraints =>
  VOICE_NOTE_BASIC_CONSTRAINTS;

export const shouldRetryVoiceNoteWithBasicAudio = (error: unknown) => {
  const errorName =
    typeof error === "object" && error && "name" in error
      ? String((error as { name?: unknown }).name || "")
      : "";
  const errorMessage =
    typeof error === "object" && error && "message" in error
      ? String((error as { message?: unknown }).message || "").toLowerCase()
      : "";

  return (
    errorName === "OverconstrainedError" ||
    errorName === "ConstraintNotSatisfiedError" ||
    (errorName === "TypeError" && errorMessage.includes("constraint")) ||
    errorMessage.includes("echo cancellation") ||
    errorMessage.includes("noise suppression") ||
    errorMessage.includes("auto gain")
  );
};

export async function requestVoiceNoteMediaStream(
  mediaDevices: MediaDevicesLike | undefined,
): Promise<MediaStream> {
  if (!mediaDevices?.getUserMedia) {
    throw new Error("voice-note-unsupported");
  }

  try {
    return await mediaDevices.getUserMedia(getVoiceNoteMediaConstraints());
  } catch (error) {
    if (!shouldRetryVoiceNoteWithBasicAudio(error)) {
      throw error;
    }

    return mediaDevices.getUserMedia(getBasicVoiceNoteMediaConstraints());
  }
}

export const getMicrophonePermissionErrorMessage = (error: unknown) => {
  const errorName =
    typeof error === "object" && error && "name" in error
      ? String((error as { name?: unknown }).name || "")
      : "";

  if (errorName === "NotAllowedError" || errorName === "PermissionDeniedError") {
    return "Microphone permission is required to record a voice note.";
  }

  if (errorName === "NotFoundError" || errorName === "DevicesNotFoundError") {
    return "No microphone was found on this device.";
  }

  if (errorName === "NotReadableError" || errorName === "TrackStartError") {
    return "Your microphone is busy or unavailable right now.";
  }

  if (errorName === "OverconstrainedError" || errorName === "ConstraintNotSatisfiedError") {
    return "This device rejected the preferred microphone settings. Please try again.";
  }

  if (error instanceof Error && error.message === "voice-note-unsupported") {
    return "Voice notes are not supported on this device.";
  }

  return "Unable to access the microphone for voice notes.";
};